import { useState, useCallback } from "react";
import { useUser } from "@clerk/clerk-react";
import {
  startChat,
  sendMessageStream,
  getConversationById,
} from "../services/chatService";
import { extractTextFromContent } from "../utils/messageUtils";

export const useChat = () => {
  const { user } = useUser();
  const userId = user?.id;

  const [chatHistory, setChatHistory] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadConversation = useCallback(async (convId) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await getConversationById(convId);
      const conversation = response.data || response;
      const messages = conversation.messages || [];

      const formattedHistory = messages.map((msg) => ({
        role: msg.role === "user" ? "user" : "model",
        parts: [
          {
            text:
              extractTextFromContent(msg.content) ||
              extractTextFromContent(msg.text),
          },
        ],
        timestamp: msg.timestamp || msg.createdAt,
      }));

      setChatHistory(formattedHistory);
      setConversationId(convId);
    } catch (err) {
      console.error("Failed to load conversation:", err);
      setError("Failed to load conversation. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const sendChatMessage = useCallback(
    async (messageContent) => {
      if (!messageContent.trim() || isLoading) return;

      setIsLoading(true);
      setError(null);

      const userMessage = {
        role: "user",
        parts: [{ text: messageContent }],
        timestamp: new Date().toISOString(),
      };

      setChatHistory((prev) => [
        ...prev,
        userMessage,
        { role: "model", parts: [{ text: "" }] },
      ]);

      try {
        let currentConversationId = conversationId;
        let isNewConversation = false;

        if (!currentConversationId) {
          const startResponse = await startChat(userId, messageContent);
          const conversation = startResponse.data || startResponse;
          currentConversationId = conversation._id || conversation.id;

          if (!currentConversationId)
            throw new Error("Failed to create conversation");
          setConversationId(currentConversationId);
          isNewConversation = true;
        }

        let replyText = "";

        await sendMessageStream(
          currentConversationId,
          userId,
          messageContent,
          (chunk) => {
            if (chunk.content) {
              replyText += chunk.content;
              setChatHistory((prev) => {
                const updated = [...prev];
                updated[updated.length - 1] = {
                  role: "model",
                  parts: [{ text: replyText }],
                  timestamp: new Date().toISOString(),
                };
                return updated;
              });
            }
          },
          () => setIsLoading(false),
          (error) => {
            console.error("Streaming error:", error);
            setError("Failed to send message. Please try again.");
            setIsLoading(false);
            setChatHistory((prev) => {
              const updated = [...prev];
              updated[updated.length - 1] = {
                role: "model",
                parts: [
                  { text: "Sorry, I encountered an error. Please try again." },
                ],
                error: true,
                timestamp: new Date().toISOString(),
              };
              return updated;
            });
          }
        );

        return { conversationId: currentConversationId, isNewConversation };
      } catch (err) {
        console.error("Chat error:", err);
        setError("Failed to send message. Please try again.");
        setIsLoading(false);
        setChatHistory((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            role: "model",
            parts: [
              { text: "Sorry, I encountered an error. Please try again." },
            ],
            error: true,
            timestamp: new Date().toISOString(),
          };
          return updated;
        });
      }
    },
    [conversationId, userId, isLoading]
  );

  const clearChat = useCallback(() => {
    setChatHistory([]);
    setConversationId(null);
    setError(null);
  }, []);

  return {
    chatHistory,
    conversationId,
    isLoading,
    error,
    sendChatMessage,
    loadConversation,
    clearChat,
  };
};
